import BaseCheckbox from '@components/BaseCheckbox';
import PopUpBase from '@components/PopUpBase';
import usePopUp, { IPopUp } from '@hooks/usePopUp';
import { InfoOutlined, WarningAmber } from '@mui/icons-material';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import color from '@theme/Colors';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Mode } from './CameraInformation';
import { exemptVehicleTypeOptions, exemptVehicleTypeReadableMapping } from './exempt-vehicle-type-config';
import { CameraWithInfo } from '@EcamModel/model';
import { CameraNotes, CameraNoteType, ExemptVehicleType } from '@EcamModel/model/CameraNotes';
import { Paging } from '@Core';
import useMedia from '@hooks/useMedia';
import PopupReivewExemptionPermit from './PopupReivewExemptionPermit';

type Props = Omit<IPopUp, 'onConfirm'> & {
    mode: Mode | null;
    idNote: number;
    listNotes: Paging<CameraNotes>;
    camera: CameraWithInfo;
    handleAddOrUpdateNote: (
        idCamera: number,
        note: string,
        idNote?: number,
        exemptVehicleTypes?: ExemptVehicleType[]
    ) => Promise<void>;
    valueTabNotes: number;
};

const VEHICLE_KEYWORD_PATTERNS: { type: ExemptVehicleType; regex: RegExp }[] = [
    { type: ExemptVehicleType.Bus, regex: /\b(bus|buses)\b/i },
    { type: ExemptVehicleType.Hgv, regex: /\b(hgv|hgvs)\b/i },
    { type: ExemptVehicleType.Truck, regex: /\b(truck|trucks)\b/i },
    { type: ExemptVehicleType.Van, regex: /\b(van|vans)\b/i },
];

const areExemptVehicleTypesEqual = (a: ExemptVehicleType[], b: ExemptVehicleType[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
};

export default function PopUpNoteHistory(props: Props) {
    const { open, onClose, mode, camera, idNote, listNotes, valueTabNotes } = props;
    const [note, setNote] = useState<string>('');
    const [exemptVehicleTypes, setExemptVehicleTypes] = useState<ExemptVehicleType[]>([]);
    const { isMediumMD, isMobileSM, isMDOnly } = useMedia()
    const filteredNote = listNotes?.rows?.find((n) => n.Id === idNote);

    const popupReivewEexemptionPermit = usePopUp();

    const isVerifierNote = valueTabNotes === 1;

    useEffect(() => {
        if (!open) return;

        if (mode === Mode.edit) {
            setNote(filteredNote?.Notes ?? '');
            setExemptVehicleTypes(filteredNote?.ExemptVehicleTypes ?? []);
        } else {
            setNote('');
            setExemptVehicleTypes([]);
        }
    }, [open]);

    const toggleExemptVehicleType = (type: ExemptVehicleType) => {
        setExemptVehicleTypes((prev) =>
            prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
        );
    };

    const mentionedButNotSelectedLabels = useMemo(() => {
        if (!isVerifierNote) return [];
        return VEHICLE_KEYWORD_PATTERNS.filter(
            ({ type, regex }) => regex.test(note) && !exemptVehicleTypes.includes(type)
        ).map(({ type }) => exemptVehicleTypeReadableMapping[type]);
    }, [isVerifierNote, note, exemptVehicleTypes]);

    const isExemptVehicleTypesUnchanged = areExemptVehicleTypesEqual(
        exemptVehicleTypes,
        filteredNote?.ExemptVehicleTypes ?? []
    );

    return (
        <PopUpBase
            open={open}
            onClose={onClose}
            fixOverflow
            dialogProps={{
                maxWidth: 'lg',
                PaperProps: {
                    style: { minWidth: isMDOnly ? 500 : isMobileSM ? 350 : 700 },
                },
            }}
            title={<Typography variant="h4">{mode === Mode.create ? 'Add note' : 'Edit note'}</Typography>}
            desc={
                <>
                    <Stack direction={'column'} spacing={1} mt={1}>
                        <TextField
                            autoFocus
                            required
                            label="Note"
                            error={note.length > 255}
                            multiline
                            sx={{
                                '& .MuiInputBase-input': {
                                    padding: 0,
                                    borderRadius: 0,
                                },
                            }}
                            rows={8}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Enter note"
                        />
                        <Typography textAlign={'end'} color={note.length > 255 ? color.danger : color.textPrimary}>
                            {note.length} / 255
                        </Typography>
                    </Stack>

                    {isVerifierNote && (
                        <Box
                            mt={2}
                            p={1.5}
                            sx={{
                                border: `1px solid ${color.grey300}`,
                                borderRadius: 1.5,
                                bgcolor: color.grey100,
                            }}
                        >
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <InfoOutlined sx={{ fontSize: 18, color: color.grey600 }} />
                                <Typography variant="subtitle2">
                                    Exempt Vehicle Types
                                </Typography>
                            </Stack>

                            <Typography
                                variant="caption"
                                component="p"
                                color={color.grey600}
                                mt={0.5}
                                lineHeight={1.5}
                            >
                                Selected vehicle types will be automatically identified and kept in Preprocessing
                                instead of being auto-generated as PCNs, helping reduce unnecessary manual
                                cancellations.
                            </Typography>

                            <Stack direction="row" flexWrap="wrap" gap={1} mt={1.5}>
                                {exemptVehicleTypeOptions.map(({ label, value }) => {
                                    const checked = exemptVehicleTypes.includes(value);
                                    return (
                                        <Stack
                                            key={value}
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            onClick={() => toggleExemptVehicleType(value)}
                                            sx={{
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                px: 1.25,
                                                minHeight: 40,
                                                borderRadius: 1,
                                                border: `1px solid ${checked ? color.success : color.grey300}`,
                                                bgcolor: checked ? color.success50 : color.white,
                                                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                                                '&:hover': { borderColor: color.success },
                                            }}
                                        >
                                            <BaseCheckbox checked={checked} disableRipple />
                                            <Typography variant="body2" fontWeight={checked ? 600 : 400}>
                                                {label}
                                            </Typography>
                                        </Stack>
                                    );
                                })}
                            </Stack>

                            {!!mentionedButNotSelectedLabels.length && (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                    mt={1.5}
                                    p={1}
                                    sx={{ bgcolor: color.gold100, borderRadius: 1 }}
                                >
                                    <WarningAmber sx={{ fontSize: 16, color: color.gold700, mt: '2px' }} />
                                    <Typography variant="caption" color={color.gold800} lineHeight={1.5}>
                                        Note mentions {mentionedButNotSelectedLabels.join(', ')} but{' '}
                                        {mentionedButNotSelectedLabels.length > 1 ? "they're" : "it's"} not selected
                                        in Exempt Vehicle Types above.
                                    </Typography>
                                </Stack>
                            )}
                        </Box>
                    )}

                    <PopupReivewExemptionPermit {...popupReivewEexemptionPermit} />
                </>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Stack direction={'row'} spacing={2}>
                        <Button
                            sx={{ minWidth: 130 }}
                            variant="cancel"
                            onClick={() => {
                                props.onClose?.();
                            }}
                        >
                            Cancel
                        </Button>

                        {props.valueTabNotes === 1 && (
                            <Button
                                sx={{ minWidth: 130 }}
                                variant="outlined"
                                onClick={() => {
                                    popupReivewEexemptionPermit.setTrue();
                                }}
                            >
                                Review Exemption Permit
                            </Button>
                        )}
                    </Stack>

                    <Button
                        sx={{ minWidth: 130 }}
                        variant="contained"
                        disabled={
                            note.length > 255 ||
                            note === '' ||
                            (mode === Mode.edit &&
                                note === filteredNote?.Notes &&
                                (!isVerifierNote || isExemptVehicleTypesUnchanged))
                        }
                        onClick={() => {
                            const data: CameraNotes = {
                                CameraId: camera.Id!,
                                Notes: note,
                                Id: mode === Mode.edit ? idNote : undefined,
                            } as CameraNotes;
                            props.handleAddOrUpdateNote(
                                data.CameraId,
                                data.Notes,
                                data.Id,
                                isVerifierNote ? exemptVehicleTypes : undefined
                            );
                            props.onClose?.();
                        }}
                    >
                        Save
                    </Button>
                </Stack>
            }
            minWidthButton={150}
        />
    );
}
